import { useNavigate } from "react-router-dom";
import { Minus, Plus, Trash2 } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { useCart } from "@/lib/cart";
import { imgUrl, inr } from "@/lib/api";

export const QtyControl = ({ value, onChange, testid, max = 20 }) => (
  <div className="inline-flex items-center rounded-full border border-slate-200">
    <button type="button" className="grid h-8 w-8 place-items-center rounded-full hover:bg-slate-100" onClick={() => onChange(Math.max(1, value - 1))} data-testid={`${testid}-dec`} aria-label="Decrease"><Minus className="h-3 w-3" /></button>
    <span className="font-mono-num w-7 text-center text-sm" data-testid={`${testid}-value`}>{value}</span>
    <button type="button" className="grid h-8 w-8 place-items-center rounded-full hover:bg-slate-100" onClick={() => onChange(Math.min(max, value + 1))} data-testid={`${testid}-inc`} aria-label="Increase"><Plus className="h-3 w-3" /></button>
  </div>
);

export const CartDrawer = () => {
  const { open, setOpen, priced, setQty, remove } = useCart();
  const navigate = useNavigate();
  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent className="flex w-full flex-col sm:max-w-md" data-testid="cart-drawer">
        <SheetHeader><SheetTitle className="font-display">Your Cart</SheetTitle></SheetHeader>
        <div className="flex-1 space-y-4 overflow-y-auto py-4">
          {priced.items.length === 0 && <p className="text-sm text-slate-500" data-testid="cart-empty">Your cart is empty.</p>}
          {priced.items.map((l) => (
            <div key={l.sku} className="flex gap-3 rounded-xl border border-slate-200 p-3" data-testid={`cart-line-${l.sku}`}>
              <img src={imgUrl(l.image)} alt={l.name} className="h-16 w-16 rounded-lg object-cover" />
              <div className="flex-1 space-y-1.5">
                <p className="text-sm font-semibold leading-tight">{l.name}</p>
                <p className="text-xs text-slate-500">{l.size} · SKU {l.sku}</p>
                {!l.available && <p className="text-xs font-semibold text-red-600">Only {l.stock} in stock</p>}
                <div className="flex items-center justify-between">
                  <QtyControl value={l.qty} onChange={(q) => setQty(l.sku, q)} testid={`cart-qty-${l.sku}`} />
                  <span className="font-mono-num text-sm font-semibold">{inr(l.line_total)}</span>
                </div>
              </div>
              <button onClick={() => remove(l.sku)} className="self-start text-slate-400 hover:text-red-600" data-testid={`cart-remove-${l.sku}`} aria-label="Remove"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
        {priced.items.length > 0 && (
          <div className="space-y-2 border-t border-slate-200 pt-4 text-sm">
            <div className="flex justify-between"><span>Subtotal</span><span className="font-mono-num" data-testid="cart-subtotal">{inr(priced.subtotal)}</span></div>
            {priced.discount > 0 && <div className="flex justify-between text-emerald-700"><span>You save</span><span className="font-mono-num">−{inr(priced.discount)}</span></div>}
            <Button className="mt-2 h-11 w-full rounded-full" onClick={() => { setOpen(false); navigate("/checkout"); }} data-testid="cart-checkout-button">Proceed to checkout</Button>
            <p className="text-center text-xs text-slate-500">Online payment only. Cash on Delivery is not available.</p>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};
